import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as types from '../../../constants/actionTypes';
import Moment from 'moment';
import { DATE_FORMAT, GRAPH_STATUSES, MOBILE_WINDOW_BREAKPOINT } from '../../../constants/constants';
import GraphArc from '../../Common/GraphArc/GraphArc';
import Card from '../../Common/Card';
import BarCard from '../../Common/BarCard/BarCard';
import Notification from '../../Common/Notification';
import Icon from '../../Common/Icon';
import Button from '../../Common/Button';
import TaskDetailManager from '../../TaskList/TaskDetail/TaskDetailManager';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import './milestones.scss';

export class Milestones extends Component {

    constructor(props, context) {
        super(props, context);

        this.state = {
            isLoadingGraph: true,
            isLoadingListing: true,
            showMore: true,
            isTaskDetailOpen: false,
            editTaskId: null,
            milestonesGraph: {},
            milestoneTasks: [],
            locationUsers: [],
            searchParams: {
                pageSize: 8,
                pageNumber: 1,
                locationKey: this.props.locationKey,
                currentDate: Moment.utc().format(DATE_FORMAT),
                statusName: ""
            }
        }
    }

    componentDidMount = async () => {
        this.init();
    }

    componentDidUpdate(prevProps) {
        // reload when the user changes locations
        // they could be searching for a location so make sure there is a value
        if (this.props.locationKey && this.props.locationKey !== prevProps.locationKey) {
            this.init();
        }
    }

    init = () => {
        const defaultParams = {
            pageNumber: 1,
            locationKey: this.props.locationKey,
            currentDate: Moment.utc().format(DATE_FORMAT),
            statusName: ""
        };
        this.setState({
            milestoneTasks: [],
            milestonesGraph: {},
            isLoadingGraph: true,
            isLoadingListing: true,
            searchParams: { ...this.state.searchParams, ...defaultParams }
        }, async () => {
            await Promise.all([this.getResponsibleUsersForLocations(), this.loadMilestones(), this.loadListing()]);
        })
    }

    getResponsibleUsersForLocations = async () => {
        try {
            const response = await this.props.locationTaskActions.searchResponsibleUsers([this.props.locationKey]);
            if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_SUCCESS) {

                this.setState({ locationUsers: response.payload.users });

            } else if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadMilestones = async () => {
        if (this.props.locationKey === "") { return; }
        try {
            const response = await this.props.locationDashboardActions.loadMilestones(this.props.locationKey);
            if (response.type === types.LOAD_MILESTONES_BY_LOCATION_SUCCESS) {

                this.setState({
                    milestonesGraph: response.payload.locationMilestonesGraph
                })

            } else if (response.type === types.LOAD_MILESTONES_BY_LOCATION_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoadingGraph: false });
        }

    }

    loadListing = async () => {
        try {
            const response = await this.props.locationDashboardActions.loadMilestonesListing(this.state.searchParams);
            if (response.type === types.LOAD_MILESTONES_LISTING_SUCCESS) {
                // if at any point there are fewer than [searchParams.pageSize] results returned then we know we can't show more
                const showMore = (response.payload.milestoneTasks.length < this.state.searchParams.pageSize) ? false : true;
                const combinedTasks = this.state.milestoneTasks.concat(response.payload.milestoneTasks);
                this.setState({
                    milestoneTasks: combinedTasks,
                    showMore: showMore
                })

            } else if (response.type === types.LOAD_MILESTONES_LISTING_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoadingListing: false });
        }
    }

    onClick_LocationMilestones = async (urlParams) => {
        const newParams = {
            currentDate: (urlParams.userDate) ? urlParams.userDate.toString() : null, // could be null depending on status
            statusName: (urlParams.statusName) ? urlParams.statusName.toString() : null, // null if clicked on total
            pageNumber: 1, // reset when switching how the listing is filtered
        }
        this.setState({
            isLoadingListing: true,
            milestoneTasks: [],
            searchParams: {
                ...this.state.searchParams, ...newParams
            }
        },
            this.loadListing
        );
    }

    getTaskStatus = (task) => {
        /**
         * 1482: The left hand edge of the card should show green if the task is
         * associated with Bar Graph status of Complete. Red if the target date
         * is prior to today’s date (based on logged-in-user's date).
         * 
         * Otherwise gray.
         */
        if (task.coreStatusName === GRAPH_STATUSES.Complete.label) {
            return "success";
        } else if (Moment.utc(task.targetDate) < Moment.utc()) {
            return "danger";
        }
        return null;
    }

    onCardClick = (task) => {
        this.setState({ isTaskDetailOpen: true, editTaskId: task.id });
    }

    onTaskDetailComplete = () => {
        this.setState({ isTaskDetailOpen: false });
        this.init();
    }

    onTaskDetailCancel = () => {
        this.setState({ isTaskDetailOpen: false });
    }

    onShowMore = () => {
        const newParams = {
            pageNumber: this.state.searchParams.pageNumber + 1
        }
        this.setState({
            isLoadingListing: true,
            searchParams: {
                ...this.state.searchParams, ...newParams
            }
        },
            this.loadListing
        );
    }

    isMobile = () => {
        return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
    }

    render() {
        return (
            <div className="milestones-widget">
                <div className="row">
                    <div className="col-12">
                        <div className="dashboard-widget-title">Milestones</div>
                    </div>
                </div>

                <div className="row mt-2">
                    {!this.state.isLoadingGraph &&
                        <div className="col-md-3 col-sm-12 quarter-card">
                            <Card>
                                <GraphArc title="Location Milestones"
                                    data={this.state.milestonesGraph.data}
                                    totalUrlParams={this.state.milestonesGraph.totalUrlParams}
                                    onClick={this.onClick_LocationMilestones}
                                />
                            </Card>
                        </div>
                    }
                    <div className="col-md-1 col-sm-12">&nbsp;</div>

                    <div className="col-md-8 col-sm-12">
                        <LoadingIndicator isLoading={this.state.isLoadingListing}>
                            {this.state.milestoneTasks && this.state.milestoneTasks.map(task =>
                                <BarCard key={task.id} handleClass={this.getTaskStatus(task)}>
                                    <div className="milestone-card" onClick={() => this.onCardClick(task)}>
                                        <div className="col-9">
                                            {task.name ?
                                                <div className="name">
                                                    {task.name}
                                                </div>
                                                :
                                                <div className="name"></div>
                                            }
                                            <div className="title">
                                                <div><Icon name="bolt me-2" stylePrefix='far' />{task.statusName}</div>
                                                {task.responsibleUser &&
                                                    <div><Icon name="user me-2 ms-4" stylePrefix='far' />{task.responsibleUser}</div>
                                                }
                                                {task.projectName &&
                                                    <div><Icon name="calendar me-2 ms-4" stylePrefix='far' />{task.projectName}</div>
                                                }
                                            </div>
                                        </div>
                                        <div className="col-2 align-self-center">
                                            <div className="dateCell">{Moment.utc(task.targetDate).format(DATE_FORMAT)}</div>
                                        </div>
                                        <div className="col-1 align-self-center">
                                            <Icon name="chevron-right" />
                                        </div>
                                    </div>

                                </BarCard>
                            )}
                            {
                                this.state.showMore &&
                                <div className="showMore">
                                    <Button type="button" text="Show More" bootstrapClass="link" name="showMore" onClick={this.onShowMore} />
                                </div>
                            }
                        </LoadingIndicator>
                    </div>
                </div>
                {
                    this.state.isTaskDetailOpen &&
                    <TaskDetailManager
                        taskId={this.state.editTaskId}
                        locationKey={this.props.locationKey}
                        userId={this.props.user.id}
                        responsibleUsers={this.state.locationUsers}
                        customerRole={this.props.user.customerRole}
                        onComplete={this.onTaskDetailComplete}
                        onCancel={this.onTaskDetailCancel}
                        filesActions={this.props.filesActions}
                        locationTaskActions={this.props.locationTaskActions}
                        isMobile={this.isMobile()}
                    />
                }
            </div>
        )
    }
}

Milestones.propTypes = {
    locationKey: PropTypes.string, // not required - user could be searching for a location
    location: PropTypes.object,
    user: PropTypes.object.isRequired,
    locationDashboardActions: PropTypes.object.isRequired,
    locationTaskActions: PropTypes.object.isRequired,
    filesActions: PropTypes.object.isRequired,
    windowWidth: PropTypes.number,
};

function mapStateToProps(state) {
    return {
        user: state.user,
        windowWidth: state.window.width
    };
}

export default connect(mapStateToProps, null)(Milestones);